import { Navigate } from 'react-router-dom';

import { LoginForm } from '@features/auth/components/LoginForm';
import { useAuthStore } from '@features/auth/store/authStore';

// ─── Left-panel illustration (mirrors the onboarding panel) ───────────────────

function AppointmentRow({
  initials, name, time, service, status,
}: {
  initials: string;
  name: string;
  time: string;
  service: string;
  status: 'done' | 'active' | 'upcoming';
}) {
  const dotColor =
    status === 'done' ? '#6ee7b7' : status === 'active' ? '#fde68a' : 'rgba(255,255,255,0.2)';
  const bg =
    status === 'active' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)';
  const ring = status === 'active' ? '1px solid rgba(255,255,255,0.18)' : 'none';

  return (
    <div
      className="flex items-center gap-3 rounded-lg px-3 py-2.5"
      style={{ background: bg, outline: ring }}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
        style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate text-[13px] font-medium"
          style={{ color: status === 'upcoming' ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.9)' }}
        >
          {name}
        </div>
        <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {service} · {time}
        </div>
      </div>
      <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: dotColor }} />
    </div>
  );
}

function SidebarIllustration() {
  return (
    <div className="relative flex flex-1 items-center justify-center overflow-hidden px-6">
      {/* Decorative circles */}
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-28 -left-16 h-80 w-80 rounded-full"
        style={{ background: 'rgba(255,255,255,0.04)' }}
      />
      <div
        className="pointer-events-none absolute right-8 bottom-24 h-32 w-32 rounded-full"
        style={{ background: 'rgba(255,255,255,0.03)' }}
      />

      {/* Dashboard card */}
      <div className="relative w-full max-w-[360px]">
        {/* Floating badge – top right */}
        <div
          className="absolute -right-3 -top-4 z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
          style={{ background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.85)' }}
        >
          <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
            <path d="M2 6 L5 9 L10 3" stroke="#6ee7b7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          SMS sent
        </div>

        {/* Card body */}
        <div
          className="rounded-2xl p-5"
          style={{
            background: 'rgba(255,255,255,0.09)',
            border: '1px solid rgba(255,255,255,0.13)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-[13px] font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                Today's schedule
              </p>
              <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Saturday, 25 Apr · 3 appointments
              </p>
            </div>
            <div
              className="flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            >
              <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
                <rect x="2" y="3" width="12" height="11" rx="2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" />
                <path d="M5 1v4M11 1v4M2 7h12" stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          <div className="space-y-1.5">
            <AppointmentRow initials="IP" name="Ion Popescu"  time="09:00" service="Physiotherapy" status="done"     />
            <AppointmentRow initials="MA" name="Maria Alexe"  time="10:30" service="Massage"       status="active"   />
            <AppointmentRow initials="AV" name="Andrei Vlad"  time="14:00" service="Kinotherapy"   status="upcoming" />
          </div>

          <div
            className="mt-4 grid grid-cols-3 gap-2 border-t pt-4"
            style={{ borderColor: 'rgba(255,255,255,0.10)' }}
          >
            {[
              { value: '12', label: 'Today' },
              { value: '94%', label: 'Attendance' },
              { value: '8', label: 'Pending' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <div className="text-base font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {value}
                </div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating badge – bottom left */}
        <div
          className="absolute -bottom-4 -left-3 z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
          style={{ background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.85)' }}
        >
          <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3" aria-hidden="true">
            <path d="M6 9V3M3 6l3-3 3 3" stroke="#6ee7b7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          94% attendance rate
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeAccount = useAuthStore((s) => s.activeAccount);

  if (isAuthenticated && activeAccount) return <Navigate to="/account" replace />;

  return (
    <div className="flex min-h-screen">
      {/* Left panel – brand teal, visible on lg+ only */}
      <div
        className="hidden w-[540px] shrink-0 flex-col py-10 lg:flex"
        style={{ backgroundColor: 'hsl(160 51% 24%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-10">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
              <rect x="2"  y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.9"  />
              <rect x="11" y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="2"  y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="11" y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.25" />
            </svg>
          </div>
          <span className="text-[15px] font-medium tracking-wide text-white">calendo</span>
        </div>

        {/* Illustration */}
        <SidebarIllustration />

        {/* Tagline */}
        <div className="px-10 pb-2">
          <p className="text-base font-semibold leading-snug text-white">
            Clinic management
            <br />
            <em className="font-light italic" style={{ color: 'rgba(255,255,255,0.65)' }}>
              that stays out of the way.
            </em>
          </p>
        </div>
      </div>

      {/* Right panel – form */}
      <div className="flex flex-1 flex-col items-center justify-center bg-background px-6 py-12 lg:px-16">
        {/* Mobile logo (hidden on lg where left panel shows) */}
        <div className="mb-8 flex items-center gap-2 lg:hidden">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-sm"
            style={{ backgroundColor: 'hsl(160 51% 24%)' }}
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
              <rect x="2"  y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.9"  />
              <rect x="11" y="2"  width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="2"  y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.55" />
              <rect x="11" y="11" width="7" height="7" rx="1.5" fill="white" opacity="0.25" />
            </svg>
          </div>
          <span className="text-[15px] font-medium tracking-wide text-foreground">calendo</span>
        </div>

        <div className="w-full max-w-[460px] rounded-md border border-border bg-surface p-8 shadow-lg">
          <LoginForm />
        </div>
      </div>
    </div>
  );
}
